import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { CtaButton } from "@/components/site/CtaButton";
import { Marquee } from "@/components/site/Marquee";
import { IMAGES } from "@/lib/assets";
import { Seo } from "@/components/Seo";

const EXPERIENCE = [
  ["01", "CHECK-UP", "I examine your business. Free, 30–45 minutes — and the questions adapt to your answers, so I only ask what can materially change the diagnosis."],
  ["02", "DIAGNOSIS", "I show you what's wrong and where the opportunity is — ranked by impact, with an estimated annual value and a confidence level attached. Ranges, not promises."],
  ["03", "PRESCRIPTION", "I tell you what I recommend, and why — including at least 5 fixes immediately actionable, free. Yours to keep, whether or not you ever buy anything from me."],
  ["04", "THE CALL", "You speak directly with me. We walk through your diagnosis together — your health score, your biggest opportunities, the numbers. No pitch, no pressure."],
  ["05", "TREATMENT", "I implement what makes sense. Only what the diagnosis justifies — scoped from the treatment library, quoted after your diagnosis, never before."],
  ["06", "AI CARE", "I keep it running, improving and evolving. Your business changes — new services, new staff, new bottlenecks — and your AI is adjusted to keep up."],
];

const PRINCIPLES = [
  ["Diagnosis before prescription", "No treatment is recommended before the problem is understood. I don't look for AI — I look for what's costing you."],
  ["Ranges, not promises", "Every estimate comes as conservative, expected and upside — each with a confidence level. I never promise exact ROI."],
  ["Yours to keep", "The diagnosis and the prescription belong to you either way. Sometimes the right answer is a tool you already have. Sometimes it's do nothing."],
  ["The honest guarantee", "I find 5 hours a week — or I tell you you're running clean. Either answer is a result."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About the Doctor — Nicolas Moreau | The AI Doctor"
        description="Why AI Doctor exists: 40 new AI tools every week, zero hours to evaluate them. One free examination, one clear answer — diagnosis before prescription."
      />
      <PageHero
        eyebrow="About"
        title="I'm the second opinion you get before spending on AI."
        intro="The AI Doctor exists because small businesses are drowning in AI advice and starving for a straight answer. Everyone's selling a tool. I start with an examination — and everything here begins the same way: a free AI Business Check-Up."
      />

      <section className="shell pb-24 lg:pb-32 grid lg:grid-cols-[1.1fr_0.9fr] gap-16 items-center">
        <Reveal>
          <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed">
            There are 40 new AI tools every week and roughly zero hours to evaluate them. So most owners either buy
            the wrong ones, or freeze and buy nothing. Both quietly cost you.
          </p>
          <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed mt-6">
            I've been an entrepreneur for years, and I've always been fascinated by the intersection of business,
            technology and human potential. I believe technology should give people two things back: their money
            and their time.
          </p>
          <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed mt-6">
            I founded The AI Doctor with a simple conviction: AI should make businesses better, not more
            complicated. My mission is to help businesses eliminate inefficiencies, recover lost revenue, and give
            their teams hours back every week. AI, automation and business strategy are the instruments — what
            actually matters is measurable ROI, less wasted effort, and more freedom to focus on growth.
            Technology should work for people — not the other way around.
          </p>
          <p className="text-[#a7a7a2] text-lg sm:text-xl leading-relaxed mt-6">
            That's why I do the boring part: look at how your business really works, then tell you the handful of
            tools and systems that will genuinely save you time or money. In plain English. In priority order.
            I'm a doctor, not a software vendor — if a tool you already pay for solves the problem, I tell you to
            keep it. And if the honest answer is "do nothing", that's the prescription.
          </p>
          <p className="display text-2xl sm:text-3xl text-[#f5f3ee] mt-12 leading-tight">
            The goal isn't to make you use more AI.
            <span className="text-[#5CCBF5]"> It's to give you your evenings back.</span>
          </p>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div className="border border-white/10 overflow-hidden">
            <img src={IMAGES.founderDoctor} alt="Nicolas Moreau, The AI Doctor" className="w-full h-full object-cover aspect-[4/5]" />
          </div>
        </Reveal>
      </section>

      <Marquee items={["Diagnosis First", "Walk-Ins Welcome", "No Jargon", "A Second Opinion", "Prescription Ready", "Fewer Tools, More Leverage", "Keep Your Evenings"]} />

      {/* THE EXPERIENCE */}
      <section className="shell py-28 lg:py-40" data-testid="about-experience">
        <Reveal>
          <span className="eyebrow">The Experience</span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="display mt-6 text-3xl sm:text-5xl text-[#f5f3ee] max-w-4xl">
            The complete AI Doctor experience.
          </h2>
        </Reveal>
        <div className="mt-14 border-t border-white/10">
          {EXPERIENCE.map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 0.05} className="grid grid-cols-1 lg:grid-cols-[90px_320px_1fr] gap-3 lg:gap-10 py-8 border-b border-white/10">
              <span className="font-mono text-2xl text-[#5c5c58]">{n}</span>
              <h3 className="display text-2xl text-[#f5f3ee]">{t}</h3>
              <p className="text-[#a7a7a2] text-lg leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW I WORK */}
      <section className="shell pb-28 lg:pb-40">
        <Reveal>
          <span className="eyebrow">How I Work</span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="display mt-6 text-3xl sm:text-5xl text-[#f5f3ee] max-w-4xl">
            Principles over pitches.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {PRINCIPLES.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05} className="bg-[#0E1526] p-8 lg:p-10">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#5CCBF5]">{t}</p>
              <p className="text-[#a7a7a2] mt-4 leading-relaxed text-lg">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHO I SEE */}
      <section className="border-y border-white/10 bg-[#131C31]">
        <div className="shell py-28 lg:py-36 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="eyebrow">Who I See</span>
            <h2 className="display mt-6 text-3xl sm:text-5xl text-[#f5f3ee] leading-tight">
              You don't need to be a $10M company. <span className="text-[#a7a7a2]">You need five hours a week back.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-[#a7a7a2] text-lg leading-relaxed">
              Solo operators, freelancers and small teams — roughly 1 to 15 people. Professional services, home
              services, healthcare, agencies, ecommerce, construction, hospitality. If you're doing enterprise-level
              admin on a five-person budget, you're exactly who this is for.
            </p>
            <p className="text-[#a7a7a2] text-lg leading-relaxed mt-6">
              You don't need to be technical. You don't need to already understand AI. You just need to be tired of
              losing evenings to work a system should be doing.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="shell py-28 lg:py-40 flex flex-col items-start gap-10">
        <h2 className="display text-3xl sm:text-5xl lg:text-6xl text-[#f5f3ee] max-w-4xl">
          A couple hours back a week is a bigger deal than it sounds.
        </h2>
        <CtaButton to="/apply" testid="about-cta">Get Your Free AI Check-Up</CtaButton>
        <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#5c5c58]">Free. 30–45 minutes. No card required.</p>
      </section>
    </div>
  );
}
